import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import NotificationBanner from '../../components/client/NotificationBanner';
import StatusTimeline from '../../components/client/StatusTimeline';
import HelpfulResources from '../../components/client/HelpfulResources';
import { ArrowLeft, MessageCircle, Upload } from 'lucide-react';

const statusColors: Record<string, string> = {
  under_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-primary-light text-primary',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-stone-100 text-stone-600',
  submitted: 'bg-blue-100 text-blue-800'
};

function getStatusText(s: string) {
  return s.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function ClientApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const { client } = useClientAuth();
  const [application, setApplication] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    try {
      const res = await apiService.getClientApplication(id!);
      if (res.status === 'success') setApplication(res.data.application);
      else setError('Failed to load application');
    } catch {
      setError('Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-ink-muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-ink mb-4">{error || 'Application not found'}</p>
          <Link
            to="/client/dashboard"
            className="inline-flex h-11 px-6 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-ink text-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <Link
                to="/client/dashboard"
                className="inline-flex items-center gap-2 text-stone-400 hover:text-white text-sm font-medium"
              >
                <ArrowLeft size={18} /> Dashboard
              </Link>
              <h1 className="text-xl font-bold mt-2">Application details</h1>
            </div>
            <div className="flex gap-2">
              <Link
                to={`/client/applications/${id}/messages`}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-primary text-white font-medium hover:bg-primary-dark"
              >
                <MessageCircle size={18} /> Messages
              </Link>
              <Link
                to={`/client/applications/${id}/upload`}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20"
              >
                <Upload size={18} /> Upload
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <NotificationBanner applicationId={id!} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Status */}
            <div className="rounded-2xl bg-surface-raised shadow-card p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-ink">Status</h2>
                  <p className="text-sm text-ink-muted mt-0.5">
                    Updated: {new Date(application.lastUpdated || application.updatedAt).toLocaleString()}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    statusColors[application.status] ?? 'bg-stone-100 text-stone-700'
                  }`}
                >
                  {getStatusText(application.status)}
                </span>
              </div>
              {application.notes && (
                <div className="mt-4 p-4 rounded-xl bg-surface-muted">
                  <h3 className="text-sm font-medium text-ink mb-1">Admin notes</h3>
                  <p className="text-sm text-ink-muted">{application.notes}</p>
                </div>
              )}
              {application.rejectionReason && (
                <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-100">
                  <h3 className="text-sm font-medium text-red-800 mb-1">Rejection reason</h3>
                  <p className="text-sm text-red-700">{application.rejectionReason}</p>
                </div>
              )}
            </div>

            {/* Shop */}
            <div className="rounded-2xl bg-surface-raised shadow-card p-6">
              <h2 className="text-lg font-semibold text-ink mb-4">Shop</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-ink-muted">Shop number</p>
                  <p className="text-ink">{application.shopNumber || 'N/A'}</p>
                </div>
                {application.shop && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-ink-muted">Floor</p>
                      <p className="text-ink">{application.shop.floor || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-ink-muted">Size</p>
                      <p className="text-ink">{application.shop.size ?? 'N/A'} sqm</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-ink-muted">Rent</p>
                      <p className="text-ink">{application.shop.rent != null ? `$${application.shop.rent}` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-ink-muted">Purpose</p>
                      <p className="text-ink">{application.shop.purpose || 'N/A'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Applicant */}
            <div className="rounded-2xl bg-surface-raised shadow-card p-6">
              <h2 className="text-lg font-semibold text-ink mb-4">Applicant</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-ink-muted">Name</p>
                  <p className="text-ink">{application.applicantName || application.Consent?.ApplicantName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-muted">Email</p>
                  <p className="text-ink">{application.email || application.Company?.EmailAddress || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-muted">Phone</p>
                  <p className="text-ink">{application.phoneNumber || application.Company?.PhoneNumber || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-muted">ID</p>
                  <p className="text-ink">{application.idNumber || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Company */}
            <div className="rounded-2xl bg-surface-raised shadow-card p-6">
              <h2 className="text-lg font-semibold text-ink mb-4">Company</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-ink-muted">Name</p>
                  <p className="text-ink">{application.companyName || application.Company?.CompanyName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-muted">Business type</p>
                  <p className="text-ink">{application.businessType || application.Company?.BusinessType || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-muted">Entity type</p>
                  <p className="text-ink">{application.companyType || application.Company?.EntityType || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-muted">Years in business</p>
                  <p className="text-ink">{application.yearsInBusiness ?? 'N/A'}</p>
                </div>
                {application.businessDescription && (
                  <div className="sm:col-span-2">
                    <p className="text-sm font-medium text-ink-muted">Description</p>
                    <p className="text-ink">{application.businessDescription}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Documents */}
            {(application.documents?.length > 0 || application.Images?.length > 0) && (
              <div className="rounded-2xl bg-surface-raised shadow-card p-6">
                <h2 className="text-lg font-semibold text-ink mb-4">Documents & images</h2>
                <div className="space-y-3">
                  {application.documents?.map((doc: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 rounded-xl bg-surface-muted"
                    >
                      <div>
                        <p className="text-sm font-medium text-ink">{doc.name}</p>
                        <p className="text-xs text-ink-muted">
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary font-medium text-sm hover:underline"
                      >
                        View
                      </a>
                    </div>
                  ))}
                  {application.Images?.map((url: string, i: number) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 rounded-xl bg-surface-muted"
                    >
                      <p className="text-sm font-medium text-ink">Image {i + 1}</p>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary font-medium text-sm hover:underline"
                      >
                        View
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl bg-surface-raised shadow-card p-6">
              <h3 className="text-lg font-semibold text-ink mb-4">Actions</h3>
              <div className="space-y-3">
                <Link
                  to={`/client/applications/${id}/messages`}
                  className="block w-full py-2.5 rounded-xl bg-primary text-white text-center font-medium hover:bg-primary-dark"
                >
                  Messages
                </Link>
                <Link
                  to={`/client/applications/${id}/upload`}
                  className="block w-full py-2.5 rounded-xl border border-stone-300 text-ink text-center font-medium hover:bg-surface-muted"
                >
                  Upload images
                </Link>
              </div>
            </div>

            <StatusTimeline
              status={application.status}
              submittedAt={application.submittedAt}
              reviewedAt={application.reviewedAt}
              notes={application.notes}
              rejectionReason={application.rejectionReason}
            />

            <HelpfulResources />
          </div>
        </div>
      </div>
    </div>
  );
}
